import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import MaterialsTab from "./tabs/MaterialsTab";
import ModulesTab from "./tabs/ModulesTab";
import ProjectsTab from "./tabs/ProjectsTab";
import ExamsTab from "./tabs/ExamsTab";
import ChatTab from "./tabs/ChatTab";
import GradebookTab from "./tabs/GradebookTab";
import CSVImport from "../components/CSVImport";
import { ArrowLeft, Users, BookOpen, GraduationCap, MessageCircle, FileText, BarChart3, Layers } from "lucide-react";

const TABS = [
  { key: "modules", label: "Modules", icon: Layers },
  { key: "materials", label: "Materials", icon: BookOpen },
  { key: "projects", label: "Projects", icon: FileText },
  { key: "exams", label: "Exams", icon: GraduationCap },
  { key: "chat", label: "Chat", icon: MessageCircle },
  { key: "gradebook", label: "Gradebook", icon: BarChart3 },
];

export default function CoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [tab, setTab] = useState("modules");
  const [error, setError] = useState("");
  const [students, setStudents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/courses/${courseId}`);
        setCourse(res.data);
        if (user.role === "instructor") {
          const s = await api.get(`/courses/${courseId}/students`);
          setStudents(s.data);
        }
      } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, [courseId, user.role]);

  if (error) return <div className="min-h-screen"><Navbar /><div className="max-w-5xl mx-auto p-10"><p className="text-red-600">{error}</p></div></div>;
  if (!course) return <div className="min-h-screen"><Navbar /><div className="max-w-5xl mx-auto p-10">Loading...</div></div>;

  const isInstructor = user.role === "instructor";
  const canAccess = isInstructor || course.enrolled;

  if (!canAccess) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto p-10">
          <button onClick={() => navigate("/dashboard")} className="text-sm flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="nb-card-static p-8 text-center">
            <h1 className="font-display text-3xl font-bold">Enroll to access this course</h1>
            <p className="text-[var(--muted-text)] mt-2">You need to be enrolled to view materials, exams and chat.</p>
            <button data-testid="course-page-enroll-btn" onClick={async () => { await api.post(`/courses/${courseId}/enroll`); window.location.reload(); }} className="nb-btn nb-btn-yellow mt-5">Enroll now</button>
          </div>
        </div>
      </div>
    );
  }

  const colorClass = "bg-[var(--cream)] text-[var(--navy-on-gold)]";

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <button data-testid="back-to-dashboard" onClick={() => navigate("/dashboard")} className="text-sm flex items-center gap-1 mb-4 hover:underline"><ArrowLeft className="w-4 h-4" /> All courses</button>

        <header className={`${colorClass} border-2 border-[var(--gold)] rounded-2xl p-7 shadow-[6px_6px_0_0_var(--gold)] animate-pop [&_h1]:text-[var(--navy-on-gold)]`}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <span className="font-mono text-sm font-bold uppercase tracking-wider text-[var(--navy-on-gold)]">{course.code}</span>
              <h1 className="font-display text-4xl md:text-5xl font-bold mt-2 leading-tight">{course.title}</h1>
              <p className="mt-3 text-base max-w-2xl text-[var(--navy-on-gold)]">{course.description}</p>
              <p className="mt-3 text-sm text-[var(--navy-on-gold)] font-semibold">Taught by Dr. Ramzy Baroody</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 border-2 border-[var(--navy-on-gold)] bg-[var(--paper)] text-[var(--gold)] rounded-full px-3 py-1.5 text-sm font-bold">
                <Users className="w-4 h-4" /> {course.student_count}/50 students
              </div>
            </div>
          </div>
        </header>

        <nav className="flex flex-wrap gap-2 mt-6">
          {TABS.map((t) => (
            <button data-testid={`tab-${t.key}`} key={t.key} onClick={() => setTab(t.key)} className={`nb-tab flex items-center gap-2 ${tab === t.key ? "active" : ""}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
          {isInstructor && (
            <button data-testid="tab-students" onClick={() => setTab("students")} className={`nb-tab flex items-center gap-2 ${tab === "students" ? "active" : ""}`}>
              <Users className="w-4 h-4" /> Students ({students.length})
            </button>
          )}
        </nav>

        <section className="mt-6">
          {tab === "modules" && <ModulesTab courseId={courseId} onGoToExams={() => setTab("exams")} onGoToProjects={() => setTab("projects")} />}
          {tab === "materials" && <MaterialsTab courseId={courseId} />}
          {tab === "projects" && <ProjectsTab courseId={courseId} />}
          {tab === "exams" && <ExamsTab courseId={courseId} />}
          {tab === "chat" && <ChatTab courseId={courseId} />}
          {tab === "gradebook" && <GradebookTab courseId={courseId} />}
          {tab === "students" && isInstructor && (
            <div>
              <CSVImport courseId={courseId} onImported={async () => {
                const s = await api.get(`/courses/${courseId}/students`);
                setStudents(s.data);
              }} />
              <div className="nb-card-static p-5">
              <h3 className="font-display text-xl font-bold mb-3">Enrolled students</h3>
              {students.length === 0 && <p className="text-sm text-[var(--muted-text)]">No one has enrolled yet.</p>}
              <ul className="divide-y-2 divide-[var(--gold)]/30">
                {students.map((s) => (
                  <li key={s.id} className="py-3 flex justify-between text-sm">
                    <div>
                      <p className="font-semibold">{s.user_name}</p>
                      <p className="text-xs text-[var(--muted-text)]">{s.user_email}</p>
                    </div>
                    <span className="text-xs text-[var(--muted-text)]">Joined {new Date(s.enrolled_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
